import React, { useState, useRef } from 'react';
import { getQueryParam } from 'common/utils/frontUtils';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Theme,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { useTranslation } from 'services/localization/useTranslation';
import AdyenCardForm from './AdyenCardForm';
import { AdyenPaymentMethod } from 'common/modules/payments/types';

export interface PaymentSubmit {
	submit: (method: AdyenPaymentMethod) => void;
}

interface RenderProps {
	openDialog: () => void;
}
interface Props {
	children?: (renderProps: RenderProps) => JSX.Element;
}

const AddCardDialog = (props: Props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const transactionId = getQueryParam('transactionId');
	const [isAddCardDialogOpen, setIsAddCardDialogOpen] = useState<boolean>(!!transactionId);

	const payBtnRef = useRef<PaymentSubmit>(null);
	const classes = useStyles();

	const saveCard = () => {
		if (payBtnRef.current) {
			payBtnRef.current?.submit('CARD_ONLINE');
		}
	};

	const handleCancel = () => {
		setIsAddCardDialogOpen(false);
	};

	const openDialog = () => {
		setIsAddCardDialogOpen(true);
	};

	return (
		<>
			{props.children ? (
				props.children({ openDialog })
			) : (
				<Button variant="outlined" color="primary" onClick={openDialog}>
					{t('shop.billingView.paymentCards.addCard', '+ Add')}
				</Button>
			)}

			<Dialog fullScreen={isMobile} maxWidth="xs" open={isAddCardDialogOpen} scroll="body">
				<DialogTitle id="confirmation-dialog-title" className={classes.dialogTitle}>
					{t('shop.billingView.paymentCards.addCardDialog.title', 'Add payment card')}
				</DialogTitle>
				<DialogContent className={classes.dialogContent}>
					<AdyenCardForm ref={payBtnRef} setDialogOpen={setIsAddCardDialogOpen} />
				</DialogContent>
				<DialogActions className={classes.dialogActions}>
					<Button onClick={handleCancel} color="secondary">
						{t('shop.billingView.paymentCards.addCardDialog.cancel', 'Cancel')}
					</Button>
					<Button onClick={saveCard} color="primary" variant="contained">
						{t('shop.billingView.paymentCards.addCardDialog.confirm', 'Add')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dialogTitle: {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
		},
		dialogContent: {
			padding: `${theme.spacing(5)} !important`,
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(2),
			},
		},
		dialogActions: {
			padding: theme.spacing(0, 5, 5, 5),
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(1),
			},
		},
	}),
);

export default AddCardDialog;
